import type { ImapSettings } from 'src/types/checkout';

import { z as zod } from 'zod';
import React, { useEffect } from 'react';
import { useBoolean } from 'minimal-shared/hooks';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Box,
  Switch,
  Tooltip,
  Typography,
  IconButton,
  InputAdornment,
  FormControlLabel,
} from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import { useChannelContext } from '../../hooks';
import { RenderPrevNextBtn } from '../render-prevnext-btn';

// imapEnabled            Boolean  @default(false)
// imapAddress            String   @default("")
// imapPort               Int      @default(0)
// imapLogin              String   @default("")
// imapPassword           String   @default("")
// imapEnableSsl          Boolean  @default(true)

const fldList = [
  { name: 'imapEnabled', required: false },
  { name: 'imapAddress', required: true },
  { name: 'imapPort', required: true },
  { name: 'imapLogin', required: true },
  { name: 'imapPassword', required: true },
  { name: 'imapEnableSsl', required: false },
];

const defaultValues: ImapSettings = {
  imapEnabled: true,
  imapAddress: '',
  imapPort: 0,
  imapLogin: '',
  imapPassword: '',
  imapEnableSsl: true,
};

export const EmailIMAP = () => {
  const { t } = useTranslate('translation');
  const showPassword = useBoolean();

  const { handleIMAPDetails, state: channelState } = useChannelContext();

  const ImapSettingSchema = validationSchema(fldList, t).superRefine((data, ctx) => {
    const imapData = data as ImapSettings; // Explicitly cast to SmtpSettings

    if (imapData.imapEnabled) {
      // Check each field and add an error if it is invalid
      if (!imapData.imapAddress.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapAddress'],
          message: `${t('IMAP_ADDRESS')} ${t('IS_REQUIRED')}`,
        });
      }

      if (!imapData.imapPort || imapData.imapPort <= 0) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapPort'],
          message: t('PORT_POSITIVE_NUMBER'),
        });
      }

      if (!imapData.imapLogin.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapLogin'],
          message: `${t('IMAP_LOGIN')} ${t('IS_REQUIRED')}`,
        });
      }

      if (!imapData.imapPassword.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapPassword'],
          message: `${t('IMAP_PASSWORD')} ${t('IS_REQUIRED')}`,
        });
      }
    }
  });

  const methods = useForm<ImapSettings>({
    mode: 'all',
    resolver: zodResolver(ImapSettingSchema),
    defaultValues: channelState.imapDetails,
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    watch,
  } = methods;

  const watchImapEnabled = watch('imapEnabled');

  const onSubmit = handleSubmit((data) => {
    if (!data.imapEnabled) {
      const defaultValObj = {
        imapAddress: '',
        imapPort: 0,
        imapLogin: '',
        imapPassword: '',
        imapEnableSsl: true,
      };

      handleIMAPDetails({ ...defaultValObj, imapEnabled: false });
      reset({ ...defaultValObj, imapEnabled: false });
    } else {
      handleIMAPDetails(data);
    }
  });

  const btnDisableHandler = () => {
    if (watchImapEnabled) {
      const {
        imapAddress: address,
        imapPort: port,
        imapLogin: login,
        imapPassword: password,
      } = watch();

      return !address || !port || !login || !password;
    }
    return false;
  };

  useEffect(() => {
    if (!watchImapEnabled) clearErrors();
  }, [watchImapEnabled]);

  const renderFields = () => (
    <Box sx={{ width: '60%', alignSelf: 'end' }}>
      {watchImapEnabled && ( // Only show fields if ImapEnabled is true
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <Field.Text
            name="imapAddress"
            label={t('ADDRESS')}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Field.Number
            name="imapPort"
            label={t('PORT')}
            type="number"
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Field.Text
            name="imapLogin"
            label={t('LOGIN')}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Field.Text
            name="imapPassword"
            label={t('PASSWORD')}
            type={showPassword.value ? 'text' : 'password'}
            autoComplete="off"
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                inputProps: {
                  autoComplete: 'new-password',
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={showPassword.value ? `${t('HIDE_PASSWORD')}` : `${t('SHOW_PASSWORD')}`}
                    >
                      <IconButton onClick={showPassword.onToggle} edge="end">
                        <Iconify
                          icon={showPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
              htmlInput: { autoComplete: 'off' },
            }}
          />

          <FormControlLabel
            control={
              <Controller
                name="imapEnableSsl"
                control={control}
                render={({ field }) => (
                  <Switch
                    {...field}
                    color="primary"
                    checked={field.value} // Bind the checked state to the form value
                    onChange={(e) => field.onChange(e.target.checked)} // Update the form value on change
                  />
                )}
              />
            }
            label={<Typography variant="body1">{t('IMAP_ENABLE_SSL')}</Typography>}
          />
        </Box>
      )}
    </Box>
  );

  const enableProviderConfigTitle = t('ENABLE_PROVIDER_CONFIGURATION').replace(
    '{PROVIDER_NAME}',
    t('IMAP')
  );

  const sendProviderDetailsTitle = t('SEND_PROVIDER_DETAILS').replace('{PROVIDER_NAME}', t('IMAP'));

  return (
    <>
      {/* EmailIMAP */}
      <Form methods={methods} onSubmit={onSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, ml: 8 }}>
              <Typography variant="subtitle1">{t('IMAP')}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {sendProviderDetailsTitle}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, width: '60%' }}>
              <FormControlLabel
                control={
                  <Controller
                    name="imapEnabled"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        color="primary"
                        checked={field.value} // Bind the checked state to the form value
                        onChange={(e) => field.onChange(e.target.checked)} // Update the form value on change
                      />
                    )}
                  />
                }
                label={<Typography variant="subtitle1">{enableProviderConfigTitle}</Typography>}
              />

              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('ENABLE_IMAP_DESCRIPTION')}
              </Typography>
            </Box>
          </Box>
          {renderFields()}
          <Box sx={{ flexGrow: 1 }} />
          <RenderPrevNextBtn isNextDisabled={btnDisableHandler()} />
        </Box>
      </Form>
    </>
  );
};
